/**
 * Dashboard authentication.
 *
 * This file exists because of issue #4. Create React App inlines every
 * `REACT_APP_*` value into the built bundle, so the admin token the dashboard
 * used to carry was readable by anyone who downloaded `main.<hash>.js` — and
 * it opened every `/admin/*` endpoint. There is no way to hold a shared secret
 * in a browser build; the fix is to stop holding one.
 *
 * Instead a person signs in. `/auth/wordpress-login` checks the credentials
 * against WordPress and returns a short-lived JWT carrying that account's
 * roles, and the backend admits it to `/admin/*` only when the roles include
 * `administrator`. The token lives in sessionStorage, so it is gone when the
 * tab closes and is never shared across origins.
 */
import axios from 'axios';

export const API_URL =
  process.env.REACT_APP_API_URL || 'https://academy-ai-production.up.railway.app';

const TOKEN_KEY = 'academy_dashboard_token';
const NAME_KEY = 'academy_dashboard_user';

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // Private windows and blocked site data both throw here.
    return null;
  }
}

export function getUserName(): string | null {
  try {
    return sessionStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
}

function store(token: string, displayName: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(NAME_KEY, displayName);
  } catch {
    // Without storage the session lasts until reload, which still works.
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(NAME_KEY);
  } catch {
    /* nothing to clear */
  }
}

export class LoginError extends Error {}

/**
 * Sign in with a WordPress account.
 *
 * A member's credentials are accepted by WordPress and produce a valid token,
 * so the "not an administrator" case only shows up when that token is used.
 * We check it here, at sign-in, rather than letting every panel fail with an
 * unexplained empty state.
 */
export async function login(username: string, password: string): Promise<string> {
  let response;
  try {
    response = await axios.post(`${API_URL}/auth/wordpress-login`, { username, password });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new LoginError('That username or password was not accepted.');
    }
    throw new LoginError('Could not reach the server. Try again in a moment.');
  }

  const token: string | undefined = response.data?.access_token;
  const roles: string[] = response.data?.user?.roles || [];
  const displayName: string = response.data?.user?.display_name || username;

  if (!token) {
    throw new LoginError('The server did not return a token.');
  }
  if (roles.length === 0) {
    // Distinct from "you are not an admin": the WordPress login route
    // (/wp-json/cpw/v1/login) did not report any roles, so nothing here can
    // tell an administrator from a member. Credentials were accepted — this
    // is a server-side configuration problem, and saying so saves whoever
    // hits it from retyping a password that was never wrong.
    throw new LoginError(
      'WordPress accepted the password but returned no roles for this account, ' +
        'so the dashboard cannot confirm you are an administrator. That is a ' +
        'server configuration problem, not your password.',
    );
  }
  if (!roles.includes('administrator')) {
    throw new LoginError('That account is not an administrator.');
  }

  store(token, displayName);
  return displayName;
}

let signedOutHandler: (() => void) | null = null;

/**
 * Register what happens when the backend refuses a token.
 *
 * Deliberately separate from installing the interceptors below. Installing
 * them from a component effect looked equivalent and was not: React runs a
 * child's effects before its parent's, so `Dashboard` fired its first
 * `/admin/*` calls before `App`'s effect had installed the request
 * interceptor. Those calls went out unauthenticated and came back 401, and the
 * panels rendered empty until something refetched — measured in a browser on
 * 2026-09-18 (`/admin/documents` 401, `/admin/user-analytics` 401). The
 * interceptors are now installed at module load, before React renders
 * anything, and only this callback is registered from a component.
 */
export function onSignedOut(handler: () => void): void {
  signedOutHandler = handler;
}

/**
 * Attach the token to every call this app makes, and treat a refusal as a
 * signed-out session so the UI returns to the login form instead of silently
 * rendering empty panels.
 */
function installAuthInterceptors(): void {
  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token && (config.url || '').startsWith(API_URL)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401 || status === 403) {
        clearSession();
        signedOutHandler?.();
      }
      return Promise.reject(error);
    },
  );
}

installAuthInterceptors();
